import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import Filter from '@/common/models/query-params/Filter';
import FilterBase from '@/common/models/query-params/FilterBase';
import FilterGroup from '@/common/models/query-params/FilterGroup';
import NumericFilterComparisonOptions from '@/common/models/query-params/NumericFilterComparisonOptions';

import FilterLogicalOperator from './FilterLogicalOperator.enum';

export default class NumericFilter extends FilterBase {
  type: string;
  columnType: string;
  property: string;
  label: string;
  placeholder: string;
  comparisonOption: any;
  firstValue: number;
  secondValue: number;
  filtersToExclude: any[];
  private instance: any;

  constructor(
    columnType = TableColumnTypes.currency,
    property = '',
    label = null,
    comparisonOption = null,
    firstValue = null,
    secondValue = null,
    filtersToExclude = [],
  ) {
    super();
    this.type = 'NumericFilter';
    this.columnType = columnType;
    this.property = property;
    this.label = label;
    this.placeholder = null;
    this.comparisonOption = comparisonOption;
    this.firstValue = firstValue;
    this.secondValue = secondValue;
    this.filtersToExclude = filtersToExclude;
  }

  get isEmpty() {
    return (
      this.comparisonOption == null ||
      (this.firstValue == null && this.comparisonOption.id !== NumericFilterComparisonOptions.IsEmpty.id)
    );
  }

  get hasSecondValue() {
    return this.comparisonOption.operators.length > 1;
  }

  static fromObject(obj) {
    return super.fromObject(obj, new NumericFilter());
  }

  transformValue(x) {
    switch (this.columnType) {
      case TableColumnTypes.percentage:
        return x / 100;
      default:
        return x;
    }
  }

  getFilters() {
    if (this.comparisonOption == null) {
      return [];
    }

    if (this.comparisonOption.id === NumericFilterComparisonOptions.IsEmpty.id) {
      return [
        new FilterGroup(
          this.property
            .split('.')
            .reduce((mem, x) => [...mem, [...mem, x].join('.')], [])
            .map((x) => new Filter(x, [null], this.comparisonOption.operators[0])),
        ),
      ];
    }

    if (this.firstValue == null) {
      return [];
    }

    const filters = [
      new Filter(this.property, [this.transformValue(this.firstValue)], this.comparisonOption.operators[0]),
      ...(this.hasSecondValue
        ? [new Filter(this.property, [this.transformValue(this.secondValue)], this.comparisonOption.operators[1])]
        : []),
    ];

    return this.comparisonOption.operators.length > 1
      ? [new FilterGroup(filters, FilterLogicalOperator.AND)] // Wrap filters in group when more than one.
      : filters;
  }

  clear() {
    this.comparisonOption = null;
    this.firstValue = null;
    this.secondValue = null;
  }

  serializeForQueryString() {
    if (this.comparisonOption?.id === NumericFilterComparisonOptions.IsEmpty.id) {
      return `${this.property}~${this.columnType}~${this.comparisonOption.id}`;
    }
    if (this.comparisonOption == null || this.firstValue == null) {
      return `${this.property}~`;
    }
    return `${this.property}~${this.columnType}~${this.comparisonOption.id}~${this.firstValue}${this.hasSecondValue ? `~${this.secondValue}` : ''}`;
  }

  static deserializeFromQueryString(filter, serialized) {
    const fragments = serialized.split('~');

    const comparisonOption =
      NumericFilterComparisonOptions.all.find((x) => x.id === fragments[1]) ||
      NumericFilterComparisonOptions.all.find((x) => x.id === fragments[2]) ||
      NumericFilterComparisonOptions.Equals;

    const columnType = TableColumnTypes[fragments[1]] || TableColumnTypes.currency;
    return new NumericFilter(
      columnType,
      filter.property,
      filter.label,
      comparisonOption,
      fragments[3],
      fragments[4],
      filter.filtersToExclude,
    );
  }

  toApi() {
    return {
      type: this.type,
      column_type: this.columnType,
      property: this.property,
      label: this.label,
      placeholder: this.placeholder,
      comparison_option: this.comparisonOption,
      first_value: this.firstValue,
      second_value: this.secondValue,
      instance: this.instance,
      filters_to_exclude: this.filtersToExclude,
    };
  }

  static fromApi(apiObject) {
    const filter = new NumericFilter(
      apiObject.column_type,
      apiObject.property,
      apiObject.label,
      apiObject.comparison_option,
      apiObject.first_value,
      apiObject.second_value,
      apiObject.filters_to_exclude,
    );
    filter.instance = apiObject.instance;
    return filter;
  }
}
